import app from './app';

// 公式のリンク
const MERMAID_OFFICIAL_URL = 'https://mermaid.live/edit';
const MERMAID_LOGO_URL = '/images/mermaid-editor.png';
const SVG_VIEW_BOX_VALUE_COUNT = 4;
let mermaid_promise = null;

// Mermaid を遅延ロードする
// NOTE: 初期バンドルに含めないよう dynamic import で別チャンクに分け、最初の図を描画するときに読み込む
const loadMermaid = () => {
  if (!spat.isBrowser) return Promise.resolve(null);
  if (mermaid_promise) return mermaid_promise;

  mermaid_promise = import('mermaid').then((mermaid_module) => {
    const mermaid = mermaid_module.default || mermaid_module;

    // Mermaid の自動描画を無効化(HyperMD 側の描画タイミングに任せるため)
    mermaid.initialize?.({
      startOnLoad: false,
      theme: 'default',
      flowchart: { htmlLabels: false, useMaxWidth: false, },
      sequence: { useMaxWidth: false },
      themeVariables: { fontSize: '14px' },
    });
    return mermaid;
  }).catch((e) => {
    // 読み込みに失敗した場合は次回の描画で再試行できるようにする
    mermaid_promise = null;
    throw e;
  });
  return mermaid_promise;
};

// Mermaid Live 用URLを生成
const buildLiveEditUrl = (rawCode) => {
  const code = `${rawCode || ''}`;
  if (!code.trim()) return MERMAID_OFFICIAL_URL;

  try {
    const state = {
      code,
      mermaid: JSON.stringify({ theme: 'default', look: 'classic' }, null, 2),
      rough: false,
      updateDiagram: true,
    };
    const pako = require('pako');
    const deflated = pako.deflate(JSON.stringify(state), { level: 9 });
    return `${MERMAID_OFFICIAL_URL}#pako:${app.utils.toBase64Url(deflated)}`;
  }
  catch (e) {
    return MERMAID_OFFICIAL_URL;
  }
};

// 描画する
const renderMermaidInto = async (target, rawCode, opts = {}) => {
  const code = `${rawCode || ''}`.trim();
  if (!target || !code) return false;

  const id_prefix = opts.idPrefix || '_mermaid_id_';
  const id = app.utils.createRenderId(id_prefix);

  try {
    const mermaid = await loadMermaid();
    if (!mermaid) return false;
    // 読み込み中に描画先が破棄された場合は何もしない
    if (typeof opts.isCanceled === 'function' && opts.isCanceled()) return false;

    mermaid.render(id, code, (svgCode, bindFunctions) => {
      target.innerHTML = svgCode;
      if (typeof bindFunctions === 'function') bindFunctions(target);
      if (typeof opts.onRendered === 'function') opts.onRendered({ target, id, code });
    });
    return true;
  }
  catch (e) {
    return false;
  }
};

// SVGのレイアウトを整える
const normalizeSvgLayout = (el) => {
  const svg = el.querySelector('svg');
  if (!svg) return;

  // もし width が 100% で出力された場合は viewBox 実寸へ戻す
  // NOTE: 上下に異常な余白が出るので対策
  const width_attr = svg.getAttribute('width') || '';
  if (/%$/.test(width_attr.trim())) {
    const view_box = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (view_box.length === SVG_VIEW_BOX_VALUE_COUNT && view_box.every(Number.isFinite)) {
      svg.setAttribute('width', `${view_box[2]}`);
      svg.setAttribute('height', `${view_box[3]}`);
    }
  }

  svg.style.maxWidth = '100%';
  svg.style.height = 'auto';
  svg.style.display = 'block';
  svg.style.margin = '12px auto';
};

// プレビューモーダルを開く
const openMermaidModal = (code) => {
  spat?.modal?.open?.('modal-mermaid-preview', { code });
};

const setupMermaid = (HyperMD) => {
  if (!spat.isBrowser) return;

  // ```mermaid ...``` コードブロックを図に変換する renderer を登録
  HyperMD.FoldCode.registerRenderer({
    name: 'mermaid',
    pattern: /^mermaid$/i,
    suggested: true,
    renderer: (code, info) => {
      const el = document.createElement('div');
      el.setAttribute('class', 'hmd-fold-code-image hmd-fold-code-mermaid');
      let removed = false;
      renderMermaidInto(el, code, {
        idPrefix: '_mermaid_id_',
        isCanceled: () => removed,
        onRendered: () => {
          normalizeSvgLayout(el);

          const svg = el.querySelector('svg');
          if (svg) {
            svg.style.cursor = 'zoom-in';
            el.onclick = (event) => {
              event.preventDefault();
              event.stopPropagation();
              openMermaidModal(code);
            };
          }
          info.changed();
        },
      }).then((rendered) => {
        if (!rendered && !removed) info.changed();
      });

      info.onRemove = () => {
        removed = true;
        el.onclick = null;
      };
      return el;
    },
  }, true);
};

export default {
  setup: setupMermaid,
  renderInto: renderMermaidInto,
  officialUrl: MERMAID_OFFICIAL_URL,
  logoUrl: MERMAID_LOGO_URL,
  buildLiveEditUrl,
};
